'use client';

import { Button } from '@shared/components/shadcn';
import { Wallet, WalletOpen } from '@shared/icons';
import { useState } from 'react';
import { WalletBalance } from './components';

type WalletButtonProps = {
  balance: string;
  className?: string;
  variant?: 'primary' | 'secondary';
  isOpen?: boolean;
};

/**
 * WalletButton component displays the user's wallet balance, toggles icon state on hover or when the WalletPopover is open,
 * and is used to open the WalletPopover.
 * The props spread (`...props`) is required because WalletButton is used as a child (asChild)
 * in the WalletPopover, allowing Radix's PopoverTrigger to use it as a trigger.
 * This ensures all necessary props are forwarded for correct Radix behaviour.
 */
function WalletButton({ balance, isOpen = false, ...props }: WalletButtonProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Button
      variant={isOpen ? 'primary' : 'secondary'}
      size="s"
      className="flex flex-row gap-2 pl-3 pr-3 pt-1 pb-1 paragraph-s-strong"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-track-name="Wallet"
      {...props}
    >
      <Wallet className={isHovered || isOpen ? 'hidden' : 'block'} size="extra-small" />
      <WalletOpen className={isHovered || isOpen ? 'block' : 'hidden'} size="extra-small" />
      <WalletBalance>{balance}</WalletBalance>
    </Button>
  );
}

export { WalletButton };
